/* eslint-disable react-hooks/exhaustive-deps */
import {
  Box,
  CircularProgress,
  Grid,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { gql, useMutation, useQuery } from "@apollo/client";
import ReserveTableButton from "../../components/Reservations/ReserveTableButton";
import {
  activePromotionsForCustomer,
  addFavouriteRestaurant,
  GET_DELIVERY_FEE,
  myLoyaltyBalance,
  mostOrderedItems as MOST_ORDERED_ITEMS_QUERY,
  profile as PROFILE_QUERY,
} from "../../apollo/server";
import Analytics from "../../utils/analytics";
import { perfMark } from "../../utils/perfDebug";
import {
  RestaurantClose,
  RestaurantInfo,
  VariationModal,
} from "../../components/Modals";
import {
  CartSheet,
  RestaurantCart,
} from "../../components/RestaurantDetailComponent";
import RestaurantHero, { CHIP_ICONS } from "../../components/RestaurantDetailComponent/Storefront/RestaurantHero";
import DealBanners from "../../components/RestaurantDetailComponent/Storefront/DealBanners";
import MenuItemCard from "../../components/RestaurantDetailComponent/Storefront/MenuItemCard";
import MostOrderedCard from "../../components/RestaurantDetailComponent/Storefront/MostOrderedCard";
import HeaderSearchBar from "../../components/Header/HeaderSearchBar";
import UserContext from "../../context/User";
import ConfigurationContext from "../../context/Configuration";
import { useLocationContext } from "../../context/Location";
import { useOrderMode, ORDER_MODES } from "../../context/OrderMode";
import { useRestaurant } from "../../hooks";
import { DAYS } from "../../utils/constantValues";
import { wallParts } from "../../utils/restaurantClock";
import useStyles from "./styles";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import StarIcon from "@mui/icons-material/Star";
import RoomServiceRoundedIcon from "@mui/icons-material/RoomServiceRounded";
import LocalShippingRoundedIcon from "@mui/icons-material/LocalShippingRounded";
import EventBusyOutlinedIcon from "@mui/icons-material/EventBusyOutlined";
import ScheduleRoundedIcon from "@mui/icons-material/ScheduleRounded";
import AltNames from "../../components/MenuName/AltNames";
import FlashMessage from "../../components/FlashMessage";
import { Header, LoginHeader } from "../../components/Header";
import { modifierOptionId } from "../../utils/modifierOptionId";
import Seo from "../../components/Seo/Seo";
import { restaurantSchema, breadcrumbSchema } from "../../utils/seoSchema";
import { CateringCartDialog, CateringMenuHeader } from "../../components/Catering/Catering";
import { StopBanner, StopCartDialog, localToday } from "../../components/FoodTruck/FoodTruck";
import TruckLanding from "../../components/FoodTruck/TruckLanding";
import TruckSchedulePage from "../../components/FoodTruck/TruckSchedulePage";
import EmbedContext from "../../context/Embed";
import CateringSuggestions from "../../components/Catering/CateringSuggestions";
import { CATERING_KEY, menuIndexForKey } from "../../utils/menuLinks";
import {
  cateringCategoryKeys,
  cateringItemIds,
  cateringSettingsOf,
  readCateringGuests,
} from "../../utils/catering";
import {
  BUTTON_RESET,
  C,
  FONT_BODY,
  FOCUS_RING,
  RADIUS,
  TYPE,
  readTopBarHeight,
  topBarOffset,
} from "../../utils/designTokens";

const DELIVERY_FEE_QUERY = gql`${GET_DELIVERY_FEE}`;
const LOYALTY_BALANCE = gql`${myLoyaltyBalance}`;
const ADD_FAVOURITE = gql`${addFavouriteRestaurant}`;
const PROFILE = gql`${PROFILE_QUERY}`;

// "11:00 AM" from an openingTimes [hours, minutes] pair.
const clock = (t) => {
  const h = Number(t[0]);
  const m = Number(t[1]) || 0;
  return `${h % 12 || 12}:${String(m).padStart(2, "0")} ${h >= 12 ? "PM" : "AM"}`;
};
const minutesOf = (t) => Number(t[0]) * 60 + Number(t[1]);

const GET_RESTAURANT_PROMOS = gql`${activePromotionsForCustomer}`;

/**
 * Transform advanced menu data into the legacy format expected by the cart system.
 * Returns { menuTypes, addons, options } where:
 *  - menuTypes: array of { name, categories: [{ _id, title, foods: [legacyFood] }] }
 *  - addons: flat array of addon groups (modifier groups + option groups)
 *  - options: flat array of individual options (modifiers + option choices)
 */
function transformAdvancedMenus(advancedMenus, restaurantCategories, tz = null) {
  // Owner-arranged category order (drag & drop in the admin panel). Categories
  // not in the map (unknown names) fall to the end alphabetically.
  const categoryOrder = new Map(
    (restaurantCategories || []).map((c) => [
      (c.title || "").trim().toLowerCase(),
      c.displayOrder ?? Number.MAX_SAFE_INTEGER,
    ])
  );
  const orderOf = (title) => {
    const v = categoryOrder.get((title || "").trim().toLowerCase());
    return v === undefined ? Number.MAX_SAFE_INTEGER : v;
  };
  const byCategoryOrder = (a, b) =>
    orderOf(a.title) - orderOf(b.title) ||
    a.title.localeCompare(b.title, undefined, { sensitivity: 'base' });

  // Advanced-menu categories are name strings on the item, so the AI-translated
  // titles have to be looked up from the categories table by name.
  const categoryAlternates = new Map(
    (restaurantCategories || []).map((c) => [
      (c.title || "").trim().toLowerCase(),
      { altTitle1: c.alternateTitle1 || null, altTitle2: c.alternateTitle2 || null },
    ])
  );
  const alternatesOf = (title) =>
    categoryAlternates.get((title || "").trim().toLowerCase()) || { altTitle1: null, altTitle2: null };

  // Owner-written line under the category name ("All come with fries"). Like
  // the translated titles, it lives on the categories table and is matched to
  // the item's category name string.
  const categoryDescriptions = new Map(
    (restaurantCategories || []).map((c) => [
      (c.title || "").trim().toLowerCase(),
      c.description || null,
    ])
  );
  const descriptionOf = (title) =>
    categoryDescriptions.get((title || "").trim().toLowerCase()) || null;

  // Catering categories (backend migration 131) leave the regular menus for a
  // Catering view of their own. They are gathered from every active menu
  // whatever today's day schedule says — catering is ordered ahead. An item on
  // several menus is listed once, priced by the first menu it appears on.
  const cateringKeys = cateringCategoryKeys(restaurantCategories);
  const cateringMap = {};
  const cateringSeen = new Set();

  const addons = [];
  const options = [];
  const addonIdSet = new Set();
  const optionIdSet = new Set();

  const to12h = (time) => {
    if (!time) return ''
    const [h, m] = time.split(':').map(Number)
    const period = h >= 12 ? 'PM' : 'AM'
    const hour = h % 12 || 12
    return `${hour}:${String(m).padStart(2, '0')} ${period}`
  }

  // A menu can keep different hours on different days, run more than one window
  // in a day (9-11 AM and 4-8 PM), or not run at all on some (backend migration
  // 088). daySchedule is { MON: { open, windows: [{ start, end }] }, … }; without
  // one, the flat startTime/endTime apply every day, as before.
  const DAY_KEYS = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT']
  // Today on the restaurant's clock (utils/restaurantClock.js).
  const todayKey = DAY_KEYS[wallParts(new Date(), tz).dayIdx]
  const windowForToday = (menu) => {
    let ds = menu.daySchedule
    if (typeof ds === 'string') {
      try { ds = JSON.parse(ds) } catch (e) { ds = null }
    }
    if (!ds || typeof ds !== 'object' || Array.isArray(ds) || Object.keys(ds).length === 0) {
      return { open: true, windows: [{ start: menu.startTime || null, end: menu.endTime || null }] }
    }
    const day = ds[todayKey]
    // A day missing from a saved schedule was switched off.
    if (!day || day.open === false) return { open: false, windows: [] }
    // Menus saved before split shifts existed carry a single start/end pair.
    const windows = Array.isArray(day.windows) && day.windows.length
      ? day.windows.map((w) => ({ start: w.start || null, end: w.end || null }))
      : [{ start: day.start || null, end: day.end || null }]
    return { open: true, windows }
  }

  // "Available 9:00 AM – 11:00 AM, 4:00 PM – 8:00 PM"
  const availabilityLabel = (windows) => {
    const parts = (windows || [])
      .filter((w) => w.start && w.end)
      .map((w) => `${to12h(w.start)} – ${to12h(w.end)}`)
    return parts.length ? `Available ${parts.join(', ')}` : null
  }

  // Time of day is deliberately NOT enforced here: the storefront shows the
  // whole menu and the hours are enforced at checkout against the order's
  // scheduled time. A day the menu does not run at all IS enforced, along with
  // a holiday that closes it — on those the menu greys out.
  const menuTypes = advancedMenus
    .filter((m) => m.isActive)
    .sort((a, b) => a.displayOrder - b.displayOrder)
    .map((menu) => {
      const today = windowForToday(menu)
      const closure = menu.activeClosure || null
      const availableNow = today.open && !closure
      const timeLabel = closure
        ? `Not available - ${closure.name}`
        : !today.open
          ? 'Not available'
          : availabilityLabel(today.windows)
      const activeItems = (menu.items || []).filter(
        (i) => i.isActive && i.isAvailable !== false
      ); // Keep 86ed items, they'll be displayed as unavailable

      // Per-menu markup + rounding. Prices from the backend are already marked up
      // AND rounded PER COMPONENT for this menu, so the modal/cart just SUM them
      // (no further rounding). Because a modifier can be shared across menus with
      // DIFFERENT rules, addon/option ids must be menu-scoped so each menu carries
      // its own final prices in the global addons/options arrays (otherwise the
      // first menu's price wins).
      const menuPct = parseFloat(menu.markupPercent || 0)
      const menuRoundingMode = menu.roundingMode || 'AS_IS'
      const menuRoundingValue = parseFloat(menu.roundingValue || 0)
      const mk = (id) => `m${menu._id}_${id}` // menu-scoped key prefix

      // Group items by categoryName. Items can carry the same category in
      // different casing (AI imports use the menu's ALL-CAPS heading, manual
      // edits use Title Case), so we key the map case-insensitively to avoid
      // rendering "BOBA" and "Boba" as two separate category buttons. The
      // best-cased spelling (mixed/Title Case over ALL-CAPS over lowercase)
      // wins as the display title.
      const titleCaseScore = (s) => {
        if (s === s.toUpperCase() && s !== s.toLowerCase()) return 0; // ALL CAPS
        if (s === s.toLowerCase() && s !== s.toUpperCase()) return 1; // all lower
        return 2; // mixed / Title Case
      };
      const categoryMap = {};
      activeItems.forEach((item) => {
        const catName = (item.categoryName || "Other").trim();
        const catKey = catName.toLowerCase();
        const isCateringItem = !!(item.categoryName || "").trim() && cateringKeys.has(catKey);
        if (isCateringItem) {
          if (cateringSeen.has(String(item._id))) return;
          cateringSeen.add(String(item._id));
        }
        const targetMap = isCateringItem ? cateringMap : categoryMap;
        if (!targetMap[catKey]) {
          targetMap[catKey] = {
            _id: isCateringItem ? `adv_cat_catering_${catKey}` : `adv_cat_${menu._id}_${catKey}`,
            title: catName,
            description: descriptionOf(catName),
            ...alternatesOf(catName),
            foods: [],
          };
        } else if (titleCaseScore(catName) > titleCaseScore(targetMap[catKey].title)) {
          // A better-cased spelling of the same category showed up — adopt it.
          targetMap[catKey].title = catName;
        }

        // Build variations from pricing. Each carries how many it serves
        // (backend migration 135): per size, or the item's own number.
        let variantServes = item.variantServes || null;
        if (typeof variantServes === "string") {
          try { variantServes = JSON.parse(variantServes); } catch (e) { variantServes = null; }
        }
        const variations = [];
        if (item.pricingMode === "VARIANTS" && item.variantPrices) {
          const entries = typeof item.variantPrices === "string"
            ? Object.entries(JSON.parse(item.variantPrices))
            : Object.entries(item.variantPrices);
          entries.forEach(([key, value]) => {
            const addonIds = [];
            (item.modifierGroups || []).forEach((mg) => addonIds.push(mk(mg._id.toString())));
            (item.optionGroups || []).forEach((og) => addonIds.push(mk(`og_${og._id}`)));
            variations.push({
              _id: `${item._id}_${key}`,
              title: key,
              serves: variantServes?.[key] ?? null,
              // RAW marked-up price; the card rounds for display, the modal/cart
              // round the assembled line once. markupPercent rides along so they
              // know whether to round.
              price: parseFloat(value),
              discounted: 0,
              addons: addonIds,
            });
          });
        } else {
          const addonIds = [];
          (item.modifierGroups || []).forEach((mg) => addonIds.push(mk(mg._id.toString())));
          (item.optionGroups || []).forEach((og) => addonIds.push(mk(`og_${og._id}`)));
          variations.push({
            _id: `${item._id}_default`,
            title: "Regular",
            serves: item.serves ?? null,
            price: parseFloat(item.basePrice || 0),
            discounted: 0,
            addons: addonIds,
          });
        }

        // Collect addons (modifier groups) and options (individual modifiers).
        (item.modifierGroups || []).forEach((mg) => {
          const mgId = mk(mg._id.toString());
          const optionIds = [];
          (mg.modifiers || []).forEach((gm) => {
            if (gm.isHidden) return;
            // A member is either a plain modifier or a full menu item used as an
            // add-on (item-as-modifier). Item members carry their own nested groups.
            const isItem = gm.memberType === "ITEM" || !!gm.item;
            const ent = isItem ? gm.item : gm.modifier;
            if (!ent) return;
            // Prefix item-member option ids so they never collide with a modifier
            // that happens to share the same numeric id (separate id sequences).
            // Menu-scope it too so the same modifier on two menus with different
            // markups keeps each menu's own price.
            const optId = modifierOptionId(mk, gm, ent._id, isItem);
            optionIds.push(optId);
            if (!optionIdSet.has(optId)) {
              optionIdSet.add(optId);
              let price = parseFloat(gm.fixedPrice || 0);
              let variantPricesMap = null;
              if (gm.priceOverrideMode === "VARIANT_BASED" && gm.variantPrices) {
                variantPricesMap = typeof gm.variantPrices === "string"
                  ? JSON.parse(gm.variantPrices)
                  : gm.variantPrices;
                const vals = Object.values(variantPricesMap).map((v) => parseFloat(v));
                if (vals.length > 0) price = Math.min(...vals);
              } else if (gm.priceOverrideMode === "FREE") {
                price = 0;
              }
              options.push({
                _id: optId,
                title: ent.name,
                price,
                variantPrices: variantPricesMap,
                libraryStatus: isItem ? null : (ent.libraryStatus || null),
                // Item-as-modifier metadata: the modal renders nestedGroups for
                // customization and the order ships these as childSelections.
                ...(isItem
                  ? { isItemMember: true, itemId: ent._id, nestedGroups: ent.modifierGroups || [], itemPricingMode: ent.pricingMode, itemVariantPrices: ent.variantPrices }
                  // A plain modifier can carry follow-up groups of its own
                  // (e.g. "Pearls" then asking sweetness). Same modal, same
                  // childSelections — it just has no sub-variant to pick.
                  : ((gm.nestedGroups || []).length > 0
                    ? { nestedGroups: gm.nestedGroups }
                    : {})),
              });
            }
          });
          if (!addonIdSet.has(mgId)) {
            addonIdSet.add(mgId);
            addons.push({
              _id: mgId,
              title: mg.name,
              description: mg.description || "",
              quantityMinimum: mg.isRequired ? (mg.minSelections || 1) : 0,
              quantityMaximum: mg.maxSelections || optionIds.length,
              options: optionIds,
              allowDuplicates: mg.allowDuplicates || false,
              maxDuplicates: mg.maxDuplicates || null,
              duplicatePricingMode: mg.duplicatePricingMode || null,
              duplicatePrices: (mg.duplicatePricingMode === 'INCREMENTAL' || mg.duplicatePricingMode === 'CUSTOM') && mg.duplicatePrices
                ? (typeof mg.duplicatePrices === 'string' ? JSON.parse(mg.duplicatePrices) : mg.duplicatePrices)
                : null,
              // Flat group cost, charged once when >=1 option in this group is picked.
              additionalCost: parseFloat(mg.additionalCost || 0),
              // OPTIONAL/REQUIRED governs whether the whole group is skippable.
              groupRequirement: mg.groupRequirement || 'REQUIRED',
            });
          }
        });

        // Collect addons (option groups) and options (choices)
        (item.optionGroups || []).forEach((og) => {
          const ogId = mk(`og_${og._id}`);
          const choiceIds = [];
          (og.choices || []).forEach((choice, idx) => {
            const choiceId = mk(`og_${og._id}_choice_${idx}`);
            choiceIds.push(choiceId);
            if (!optionIdSet.has(choiceId)) {
              optionIdSet.add(choiceId);
              options.push({
                _id: choiceId,
                title: choice,
                price: 0,
              });
            }
          });
          if (!addonIdSet.has(ogId)) {
            addonIdSet.add(ogId);
            addons.push({
              _id: ogId,
              title: og.name,
              description: "",
              quantityMinimum: og.isRequired ? 1 : 0,
              quantityMaximum: og.selectionType === "MULTI" ? choiceIds.length : 1,
              options: choiceIds,
            });
          }
        });

        // Build legacy food object
        const food = {
          _id: item._id.toString(),
          // Which menu this card came from + that menu's markup/rounding rule.
          // Carried into the cart line so checkout charges the exact per-menu
          // price the customer saw (multiple menus can be active with different
          // rules). Prices are already final (rounded per-component) from backend.
          menuId: menu._id.toString(),
          markupPercent: menuPct,
          roundingMode: menuRoundingMode,
          roundingValue: menuRoundingValue,
          title: item.name,
          // AI-translated names in the owner's chosen menu languages; rendered
          // in brackets beside the title per restaurant.menuLanguageDisplay.
          altName1: item.alternateName1 || null,
          altName2: item.alternateName2 || null,
          description: item.description || "",
          image: item.imageUrl || "",
          // Carry the AI-fallback flags so cards can badge AI-generated content.
          isAiImage: item.isAiImage || false,
          isAiDescription: item.isAiDescription || false,
          isActive: true,
          is86ed: item.is86ed || false,
          // On-hand count at a food-truck stop (null = not counted).
          stockLeft: item.stockLeft ?? null,
          dietaryTags: item.dietaryTags || [],
          variations,
          // DoorDash-style per-dish stats (null unless the dish meets threshold).
          likedPercent: item.likedPercent ?? null,
          voteCount: item.voteCount ?? null,
          mostLikedRank: item.mostLikedRank ?? null,
          // Catering: whether this is a catering item, and (on a regular item)
          // the catering version the "Available for catering" button opens.
          isCatering: isCateringItem,
          cateringItemId: item.cateringItemId != null ? String(item.cateringItemId) : null,
          // How many people one feeds (backend migration 135).
          serves: item.serves ?? null,
        };

        targetMap[catKey].foods.push(food);
      });

      return {
        // A per-menu link can name the menu by id (utils/menuLinks.js).
        _id: menu._id,
        name: menu.name,
        // Categories follow the owner's dragged order from the admin panel;
        // ties/unknowns fall back to A-Z so the order stays stable.
        categories: Object.values(categoryMap).sort(byCategoryOrder),
        availableNow,
        timeLabel,
        // Every item on it was catering, so the menu has nothing left to show.
        cateringOnly: activeItems.length > 0 && Object.keys(categoryMap).length === 0,
      };
    })
    .filter((menu) => !menu.cateringOnly);

  const cateringCategories = Object.values(cateringMap).sort(byCategoryOrder);

  return { menuTypes, addons, options, cateringCategories };
}

function RestaurantDetail() {
  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.down("lg"));
  const classes = useStyles();
  const { state, search: locationSearch, key: locationKey } = useLocation();
  const query = useParams();
  const [isClose, setIsClose] = useState(false);
  const [mainError, setMainError] = useState({});
  const [addonData, setAddonData] = useState(null);
  const [reviewModal, setReviewModal] = useState(false);
  const [variationModal, setVariationModal] = useState(false);
  const [selectedMenu, setSelectedMenu] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState(null);
  // "Search this menu" — filters the open menu's dishes as you type.
  const [menuQuery, setMenuQuery] = useState("");
  // Narrow screens: the cart's bottom sheet.
  const [cartSheetOpen, setCartSheetOpen] = useState(false);
  // Open while the customer decides whether to start a new cart because the
  // item is the other kind (catering vs regular). { open, toCatering, resolve }
  const [cartKindPrompt, setCartKindPrompt] = useState(null);
  // "Our Menu" — where a catering deep link scrolls to.
  const menuSectionRef = useRef(null);
  // The sticky category chip row, and its scroller — used to keep the active
  // chip visible as the customer moves between categories.
  const chipsBarRef = useRef(null);
  const chipStripRef = useRef(null);
  const chipRefs = useRef({});
  // The Reviews carousel, moved by the CarouselArrows header control.
  const reviewsScrollerRef = useRef(null);
  const navigate = useNavigate();
  // Food truck (backend migration 145): ?truck= opens one truck's month of
  // stops, ?stop= that stop's own menu (&day= is the box it came from). With
  // neither, a food truck's page is the list of its trucks.
  const truckParams = useMemo(() => new URLSearchParams(locationSearch || ""), [locationSearch]);
  const stopId = truckParams.get("stop");
  const truckId = truckParams.get("truck");
  const truckMonth = truckParams.get("month");
  const stopDay = truckParams.get("day");
  const goTruckPage = useCallback((params) => {
    const qs = new URLSearchParams(params).toString();
    navigate(`/restaurant/${query.slug}${qs ? `?${qs}` : ""}`);
    window.scrollTo(0, 0);
  }, [navigate, query.slug]);
  const { data, loading, error } = useRestaurant(state?.id, query.slug, stopId);
  const restaurantId = state?.id || data?.restaurant?._id || null;
  const orderingStop = stopId ? data?.restaurant?.orderingStop || null : null;
  // Shown instead of the menu: the truck list, or one truck's schedule.
  const showTruckPages = data?.restaurant?.isMobile === true && !stopId;
  // Who the Catering view suggests for (backend migration 135): the event
  // planned on the Catering page, else the planner's guests, else 25.
  const [cateringGuests, setCateringGuests] = useState(25);
  useEffect(() => {
    if (restaurantId) setCateringGuests(readCateringGuests(restaurantId));
  }, [restaurantId]);
  // Debug probe (?debug=1): the promo and most-ordered queries below are skipped
  // until restaurantId exists, so they queue behind the menu query rather than
  // running alongside it. These marks make that ordering visible.
  useEffect(() => { perfMark("menu screen mounted"); }, []);
  useEffect(() => {
    if (data?.restaurant) perfMark("menu data ready, rendering items");
  }, [data]);
  const { data: promoData } = useQuery(GET_RESTAURANT_PROMOS, {
    // At a truck stop, only the deals that run there.
    variables: { restaurantId, stopId },
    skip: !restaurantId,
    fetchPolicy: 'cache-and-network',
  });
  const restaurantPromos = promoData?.activePromotionsForCustomer || [];
  const { data: mostOrderedData } = useQuery(gql`${MOST_ORDERED_ITEMS_QUERY}`, {
    variables: { restaurantId, limit: 8 },
    skip: !restaurantId,
    fetchPolicy: 'cache-and-network',
  });
  const mostOrdered = mostOrderedData?.mostOrderedItems || [];
  const configuration = useContext(ConfigurationContext);
  // Embed mode (a partner's iframe): /deals is outside it, so no "See all deals".
  const { isEmbed } = useContext(EmbedContext);
  const { location: customerLocation } = useLocationContext();
  const { orderMode } = useOrderMode() || {};
  const isPickup = orderMode === ORDER_MODES.PICKUP;

  const {
    restaurant: restaurantCart,
    setCartRestaurant,
    stashCart,
    checkItemCart,
    addCartItem,
    addQuantity,
    removeQuantity,
    cart,
    clearCart,
    isLoggedIn,
    getCartMeta,
    profile,
  } = useContext(UserContext);

  // The header's fee chip: what delivery to the customer's address costs.
  const { data: feeData } = useQuery(DELIVERY_FEE_QUERY, {
    variables: {
      restaurantId,
      latitude: parseFloat(customerLocation?.latitude),
      longitude: parseFloat(customerLocation?.longitude),
      stopId: data?.restaurant?.orderingStop?.stop?._id || null,
    },
    skip: !restaurantId || !customerLocation?.latitude || !customerLocation?.longitude || isPickup,
    fetchPolicy: "cache-first",
  });
  const deliveryQuote = feeData?.getDeliveryFee || null;
  // Same balance query as the header, so it comes from the cache.
  const { data: balanceData } = useQuery(LOYALTY_BALANCE, {
    skip: !profile,
    fetchPolicy: "cache-first",
  });
  const rewardsPoints = typeof balanceData?.myLoyaltyPoints?.balance === "number"
    ? balanceData.myLoyaltyPoints.balance
    : undefined;
  const isFavourite = !!(profile?.favourite || []).some((id) => String(id) === String(restaurantId));
  const [toggleFavourite, { loading: favouriteSaving }] = useMutation(ADD_FAVOURITE, {
    refetchQueries: [{ query: PROFILE }],
    onCompleted: () => showMessage({ type: "success", message: "Favourites updated" }),
    onError: () => showMessage({ type: "error", message: "Couldn't update favourites. Try again." }),
  });
  // Stop fields carried in the cart's meta, so checkout orders for that stop.
  const stopCartMeta = orderingStop?.stop
    ? {
        stopId: String(orderingStop.stop._id),
        stopLabel: orderingStop.stop.label || orderingStop.stop.address,
        truckName: orderingStop.truckName || null,
      }
    : {};
  // Open while the customer decides whether to start a new cart because the
  // cart holds another stop's items. { open, fromLabel, resolve }
  const [stopCartPrompt, setStopCartPrompt] = useState(null);

  // Determine if we should use advanced menus
  const useAdvancedMenus = useMemo(() => {
    const menus = data?.restaurant?.advancedMenus;
    if (!menus || menus.length === 0) return false;
    return menus.some((m) => m.isActive && m.items && m.items.length > 0);
  }, [data]);

  // Transform advanced menus into legacy-compatible format
  const advancedData = useMemo(() => {
    if (!useAdvancedMenus) return null;
    return transformAdvancedMenus(data.restaurant.advancedMenus, data.restaurant.categories, data.restaurant.timezone);
  }, [useAdvancedMenus, data]);

  // Which AI-translated names this restaurant publishes beside the English one.
  // 'NONE' (the default) renders the menu exactly as it did before.
  const langDisplay = data?.restaurant?.menuLanguageDisplay || 'NONE';

  // Legacy menu data
  const allDeals = data?.restaurant?.categories?.filter(
    (cat) => cat.foods.length
  );
  const deals = allDeals?.map((c, index) => ({
    ...c,
    index,
  }));
  const legacyMenuTypes = [
    { name: "Dinner Menu", categories: deals || [] },
    { name: "Lunch Menu", categories: deals?.slice(0, Math.ceil((deals?.length || 0) / 2)) || [] },
  ];

  // Catering (backend migration 131): the catering categories pulled out of the
  // regular menus, offered as one more menu type after them.
  const cateringMenu = useMemo(() => {
    const categories = advancedData?.cateringCategories || [];
    if (!categories.length) return null;
    return { name: "Catering", categories, availableNow: true, timeLabel: null, isCatering: true };
  }, [advancedData]);
  const cateringSettings = useMemo(() => cateringSettingsOf(data?.restaurant), [data]);
  // Every catering item id — how a cart line's kind is told (cart lines carry
  // no category).
  const cateringIds = useMemo(() => cateringItemIds(data?.restaurant), [data]);
  // Catering item id → its food + category, for "Available for catering".
  const cateringTargets = useMemo(() => {
    const map = new Map();
    (cateringMenu?.categories || []).forEach((category) =>
      category.foods.forEach((food) => map.set(String(food._id), { food, category }))
    );
    return map;
  }, [cateringMenu]);

  // Use advanced or legacy
  const menuTypes = useAdvancedMenus
    ? (cateringMenu ? [...advancedData.menuTypes, cateringMenu] : advancedData.menuTypes)
    : legacyMenuTypes;
  const cateringMenuIndex = cateringMenu ? menuTypes.length - 1 : -1;
  const isCateringView = cateringMenuIndex >= 0 && selectedMenu === cateringMenuIndex;

  // Merged addons/options for cart compatibility
  const effectiveAddons = useAdvancedMenus
    ? [...(data?.restaurant?.addons || []), ...advancedData.addons]
    : data?.restaurant?.addons || [];
  const effectiveOptions = useAdvancedMenus
    ? [...(data?.restaurant?.options || []), ...advancedData.options]
    : data?.restaurant?.options || [];

  const restaurantInfo = {
    _id: data?.restaurant._id ?? "",
    name: data?.restaurant?.name ?? "...",
    image: data?.restaurant?.image ?? "",
    deals: deals,
    reviewData: data?.restaurant?.reviewData ?? {},
    address: data?.restaurant?.address ?? "",
    deliveryCharges: data?.restaurant?.deliveryCharges ?? "",
    deliveryTime: data?.restaurant?.deliveryTime ?? "...",
    preparationTime: data?.restaurant?.preparationTime ?? null,
    isAvailable: data?.restaurant?.isAvailable ?? true,
    openingTimes: data?.restaurant?.openingTimes ?? [],
    // Its hours are read on its clock (utils/restaurantClock.js).
    timezone: data?.restaurant?.timezone ?? null,
    // Food truck: the modal shows the week's route instead of opening hours.
    isMobile: data?.restaurant?.isMobile === true,
    isOpenNow: data?.restaurant?.isOpenNow === true,
    serviceLocation: data?.restaurant?.serviceLocation ?? null,
    upcomingStops: data?.restaurant?.upcomingStops ?? [],
  };

  // Average review rating: precise mean of the actual review ratings, falling
  // back to the server's rounded `ratings`. Computed once for the header tile.
  const reviewList = restaurantInfo.reviewData?.reviews || [];
  const ratedReviews = reviewList.filter((r) => r.rating);
  const reviewAvg = ratedReviews.length
    ? ratedReviews.reduce((s, r) => s + r.rating, 0) / ratedReviews.length
    : Number(restaurantInfo.reviewData?.ratings || 0);
  // Only ratings with written text are "public reviews" — those are the cards we
  // show in the carousel. The total rating count drives the header subtitle.
  const writtenReviews = reviewList.filter((r) => (r.description || '').trim());
  const totalRatings = restaurantInfo.reviewData?.total || reviewList.length;

  // Per-restaurant document head. These are the pages that should win
  // "<restaurant name> delivery" searches, so they carry the only real
  // location signal on the site — each restaurant's own address.
  // Held back until the restaurant resolves: a title of "..." (the loading
  // placeholder in restaurantInfo) would be worse than the static fallback.
  const seo = useMemo(() => {
    const r = data?.restaurant;
    if (!r?.name || !r?.slug) return null;
    const cuisines = r.cuisines?.length ? r.cuisines.slice(0, 3).join(", ") : "";
    const description = [
      cuisines ? `${cuisines}.` : "",
      `Order from ${r.name}${r.address ? ` on ${r.address}` : ""} with RushMyFood.`,
      "Browse the full menu, then choose delivery or pickup.",
    ]
      .filter(Boolean)
      .join(" ");
    return {
      title: `${r.name} — Menu, Delivery & Pickup`,
      description,
      path: `/restaurant/${r.slug}`,
      image: r.image,
      schema: [
        restaurantSchema({
          name: r.name,
          image: r.image,
          slug: r.slug,
          address: r.address,
          cuisines: r.cuisines,
          openingTimes: r.openingTimes,
          coordinates: r.location?.coordinates,
          reviewAvg,
          reviewCount: totalRatings,
        }),
        breadcrumbSchema([
          { name: "Restaurants", path: "/restaurant-list" },
          { name: r.name, path: `/restaurant/${r.slug}` },
        ]),
      ],
    };
  }, [data?.restaurant, reviewAvg, totalRatings]);

  const toggleVariationModal = useCallback(() => {
    setVariationModal((prev) => !prev);
  }, []);

  const toggleReviewModal = useCallback(() => {
    setReviewModal((prev) => !prev);
  }, []);

  const toggleCloseModal = useCallback(() => {
    setIsClose((prev) => !prev);
  }, []);

  const isOpen = useCallback(() => {
    if (data && data.restaurant) {
      // A food truck's hours ARE its stop windows, and only the server knows
      // which stop is live right now — trust its answer over the weekly grid.
      if (data.restaurant.isMobile) return data.restaurant.isOpenNow === true;
      const openingTimes = data.restaurant.openingTimes || [];
      if (openingTimes.length < 1) return false;
      // The restaurant's clock, not the customer's.
      const wall = wallParts(new Date(), data.restaurant.timezone);
      const currentTotalMinutes = wall.minutes;
      const todaysTimings = openingTimes.find((o) => o.day === DAYS[wall.dayIdx]);
      if (todaysTimings === undefined) return false;
      const times = todaysTimings.times.filter((t) => {
        const startTotalMinutes = Number(t.startTime[0]) * 60 + Number(t.startTime[1]);
        const endTotalMinutes = Number(t.endTime[0]) * 60 + Number(t.endTime[1]);
        return currentTotalMinutes >= startTotalMinutes && currentTotalMinutes <= endTotalMinutes;
      });
      return times.length > 0;
    } else return false;
  }, [data]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(async () => {
    await Analytics.track(Analytics.events.NAVIGATE_TO_RESTAURANTS_DETAIL);
  }, []);

  // Show the "closed" modal at most once per visit. Using toggleCloseModal in a
  // [data, isOpen] effect re-fired on every Apollo refetch (isOpen is a
  // useCallback that changes identity with data), flipping the modal back open
  // even after the user closed it. Guard with a ref and SET (not toggle).
  const closedModalShownRef = useRef(false);
  useEffect(() => {
    if (closedModalShownRef.current) return;
    // A food truck says where and when on its own pages and stop banner.
    if (data?.restaurant?.isMobile) return;
    if (data?.restaurant && (!isOpen() || !data?.restaurant?.isAvailable)) {
      closedModalShownRef.current = true;
      setIsClose(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.restaurant]);

  // `suggested` ({ variationId, quantity }) opens the item on that size and
  // count — the "for your guests" suggestion on the Catering view.
  // A truck stop that isn't taking orders today: its menu can be browsed,
  // nothing added.
  const stopClosedForOrders = () => {
    if (!stopId || orderingStop?.canOrder) return false;
    showMessage({
      type: 'info',
      message: stopDay && stopDay > localToday()
        ? 'Ordering for this stop opens on the day.'
        : (orderingStop?.note || "This stop isn't taking orders right now."),
    });
    return true;
  };

  const openFoodModal = (food, suggested = null) => {
    // Only block when the restaurant is manually unavailable (offline). Being
    // outside opening hours is fine — the customer can still add items and
    // schedule the order for the earliest available time at checkout.
    if (!restaurantInfo.isAvailable) {
      toggleCloseModal();
      return;
    }
    if (stopClosedForOrders()) return;
    setAddonData({
      food,
      addons: effectiveAddons,
      options: effectiveOptions,
      restaurant: data?.restaurant._id,
      image: data?.restaurant?.image,
      suggested,
      restaurantName: data?.restaurant?.name,
      restaurantSlug: data?.restaurant?.slug,
      stopMeta: stopCartMeta,
    });
    toggleVariationModal();
  };

  // Catering and regular items never share a cart (backend migration 131).
  // Resolves { clear } — clear is true when the customer chose to start a new
  // cart with this item — or null for "Keep my cart". Only this restaurant's
  // cart is checked; a cart from another restaurant is parked as before.
  // A cart is one stop's (or the regular menu's): each stop has its own menu
  // and prices. Asked before the catering check, on this restaurant's cart only.
  const confirmCartStop = () => {
    if (!restaurantCart || String(restaurantCart) !== String(restaurantId) || !cart.length) {
      return Promise.resolve({ clear: false });
    }
    const meta = getCartMeta() || {};
    const cartStop = meta.stopId ? String(meta.stopId) : null;
    if ((cartStop || null) === (stopId ? String(stopId) : null)) return Promise.resolve({ clear: false });
    const fromLabel = cartStop
      ? [meta.truckName, meta.stopLabel].filter(Boolean).join(' at ') || 'another stop'
      : 'the regular menu';
    return new Promise((resolve) => setStopCartPrompt({ open: true, fromLabel, resolve }));
  };

  const answerStopCart = (verdict) => {
    stopCartPrompt?.resolve?.(verdict);
    setStopCartPrompt((prev) => (prev ? { ...prev, open: false, resolve: null } : prev));
  };

  const confirmCartKind = async (food, replaceKey = null) => {
    const stopVerdict = await confirmCartStop();
    if (!stopVerdict) return null;
    if (stopVerdict.clear) return { clear: true };
    if (!cateringIds.size || !restaurantCart || String(restaurantCart) !== String(restaurantId)) {
      return Promise.resolve({ clear: false });
    }
    const itemIsCatering = cateringIds.has(String(food?._id));
    const otherKind = cart.some(
      (line) => line.key !== replaceKey && cateringIds.has(String(line._id)) !== itemIsCatering
    );
    if (!otherKind) return Promise.resolve({ clear: false });
    return new Promise((resolve) =>
      setCartKindPrompt({ open: true, toCatering: itemIsCatering, resolve })
    );
  };

  const answerCartKind = (verdict) => {
    cartKindPrompt?.resolve?.(verdict);
    // Kept (closed) so the dialog's copy doesn't flip while it fades out.
    setCartKindPrompt((prev) => (prev ? { ...prev, open: false, resolve: null } : prev));
  };

  // quiet: no "added to cart" message — the −/+ steppers show the count.
  const addFoodToCart = async (food, { quiet = false } = {}) => {
    // Off-hours is allowed (scheduled order at checkout); only block if the
    // restaurant has been manually marked unavailable.
    if (!restaurantInfo.isAvailable) {
      toggleCloseModal();
      return;
    }
    if (stopClosedForOrders()) return;
    // Ordering from a different restaurant now parks the current cart instead
    // of silently refusing to add the item — the old cart is waiting under
    // Other carts at checkout.
    const switching = Boolean(restaurantCart) && food.restaurant !== restaurantCart;
    if (switching) {
      await stashCart();
      showMessage({
        type: 'info',
        message: 'Your other cart is saved. Find it at checkout.',
      });
    }
    await addToCart(food, food.restaurant !== restaurantCart, switching, quiet);
  };

  // `parked` means stashCart already emptied the cart, so the clear-on-switch
  // below must not run — clearCart would drop the copy we just parked.
  const addToCart = async (food, clearFlag, parked = false, quiet = false) => {
    const cartMeta = {
      _id: food.restaurant,
      name: data?.restaurant?.name,
      slug: data?.restaurant?.slug,
      image: data?.restaurant?.image,
      ...stopCartMeta,
    };
    if (food.variations.length === 1 && food.variations[0].addons.length === 0) {
      // Same cart-kind check the item modal makes before it adds.
      const verdict = await confirmCartKind(food);
      if (!verdict) return;
      const clear = clearFlag || verdict.clear;
      await setCartRestaurant(food.restaurant, cartMeta);
      const result = clear ? { exist: false } : checkItemCart(food._id);
      if (result.exist) await addQuantity(result.key);
      else await addCartItem(food._id, food.variations[0]._id, 1, [], clear, "", null, food.menuId);
      if (!quiet) showMessage({ type: 'success', message: `${food.title} added to cart` });
    } else {
      if (clearFlag && !parked) await clearCart();
      setAddonData({
        food,
        addons: effectiveAddons,
        options: effectiveOptions,
        restaurant: data?.restaurant._id,
        image: data?.restaurant?.image,
        // Carried so a parked cart can name the restaurant it belongs to.
        restaurantName: data?.restaurant?.name,
        restaurantSlug: data?.restaurant?.slug,
        stopMeta: stopCartMeta,
      });
      toggleVariationModal();
    }
    await Analytics.track(Analytics.events.ADD_TO_CART, {
      title: food.title,
      restaurantName: food.restaurantName,
      variations: food.variations,
    });
  };

  const showMessage = useCallback((messageObj) => {
    setMainError(messageObj);
  }, []);

  const toggleSnackbar = useCallback(() => {
    setMainError({});
  }, []);

  // Where a category heading should land: just under the top bar and the
  // sticky chip row.
  const stickyOffset = () => readTopBarHeight() + (chipsBarRef.current?.offsetHeight || 60) + 12;

  const handleSelectMenu = (index) => {
    setSelectedMenu(index);
    setSelectedCategory(null);
    // Switching menus from far down the page starts the new one at its top.
    const el = menuSectionRef.current;
    if (el && el.getBoundingClientRect().top < 0) {
      window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - readTopBarHeight() - 8, behavior: "smooth" });
    }
  };

  const handleCategoryClick = (category) => {
    // The chips list the open menu's categories, but a catering deep link or
    // "Available for catering" can name a category on another menu: switch
    // to that menu first, or its `category-${id}` element never mounts.
    const owningMenuIndex = menuTypes.findIndex((menu) =>
      (menu.categories || []).some((cat) => cat._id === category._id)
    );
    if (owningMenuIndex !== -1 && owningMenuIndex !== selectedMenu) {
      setSelectedMenu(owningMenuIndex);
    }
    if (menuQuery) setMenuQuery("");
    setSelectedCategory(category);
    // Wait for the re-render, then bring the heading in just below the
    // sticky top bar and chip row.
    const categoryId = category._id;
    setTimeout(() => {
      requestAnimationFrame(() => {
        const element = document.getElementById(`category-${categoryId}`);
        if (element) {
          const y = element.getBoundingClientRect().top + window.scrollY - stickyOffset();
          window.scrollTo({ top: y, behavior: 'smooth' });
        }
      });
    }, 100);
  };

  // "Available for catering": switch to the Catering view, bring the catering
  // version's category into view, then open it once the scroll has settled.
  const openCateringItem = (target) => {
    if (!target) return;
    handleCategoryClick(target.category);
    if (target.food.is86ed) return;
    setTimeout(() => {
      openFoodModal({
        ...target.food,
        restaurant: restaurantInfo._id,
        restaurantName: restaurantInfo.name,
        deliveryCharges: restaurantInfo.deliveryCharges,
      });
    }, 550);
  };

  // Deep link into one menu. The Catering page and catering deals navigate
  // here with state.catering, a shared link can carry ?catering=1, and a
  // per-menu link (app.rushmyfood.com/capeannpizza/breakfast) arrives as
  // ?menu=<key>. Applied once per navigation, then "Our Menu" scrolls into view.
  const linkParams = new URLSearchParams(locationSearch || "");
  const linkedMenuKey =
    state?.catering === true || linkParams.get("catering") === "1" ? CATERING_KEY : linkParams.get("menu");
  const linkedMenuIndex =
    useAdvancedMenus && linkedMenuKey ? menuIndexForKey(menuTypes, linkedMenuKey) : -1;
  // ?item=<dish id> ("Most ordered near you" on the restaurant list) opens
  // that dish once the menu is here. The finding and opening live below the
  // loading return, so the body leaves a function in this ref for the effect.
  const linkedItemId = linkParams.get("item");
  const openLinkedItemRef = useRef(null);
  const itemLinkHandledRef = useRef(null);
  useEffect(() => {
    if (!linkedItemId || itemLinkHandledRef.current === locationKey) return;
    if (openLinkedItemRef.current?.(linkedItemId)) itemLinkHandledRef.current = locationKey;
  });

  const menuLinkHandledRef = useRef(null);
  useEffect(() => {
    if (linkedMenuIndex < 0) return;
    if (menuLinkHandledRef.current === locationKey) return;
    menuLinkHandledRef.current = locationKey;
    setSelectedMenu(linkedMenuIndex);
    setSelectedCategory(null);
    setTimeout(() => {
      requestAnimationFrame(() => {
        const el = menuSectionRef.current;
        if (!el) return;
        const y = el.getBoundingClientRect().top + window.scrollY - readTopBarHeight() - 8;
        window.scrollTo({ top: Math.max(0, y), behavior: "smooth" });
      });
    }, 150);
  }, [linkedMenuIndex, locationKey]);


  const currentCategories = menuTypes[selectedMenu]?.categories || [];

  // Scroll-spy: highlight the chip of the category being read. The trigger
  // line is the same offset a chip tap scrolls to, so a heading that has just
  // landed there is the active one.
  useEffect(() => {
    if (menuQuery) return undefined;
    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        const line = stickyOffset() + 4;
        const cats = currentCategories;
        let activeId = null;
        for (const cat of cats) {
          const el = document.getElementById(`category-${cat._id}`);
          if (!el) continue;
          if (el.getBoundingClientRect().top - line <= 0) activeId = cat._id;
          else break;
        }
        // At the very bottom the last headings can't reach the line, so the
        // last category is the one being read.
        const atBottom = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 2;
        if (atBottom && cats.length && activeId) activeId = cats[cats.length - 1]._id;
        if ((activeId || null) !== (selectedCategory?._id || null)) {
          setSelectedCategory(activeId ? cats.find((c) => c._id === activeId) || null : null);
        }
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [currentCategories.length, selectedMenu, selectedCategory?._id, menuQuery]);

  // Keep the active category chip visible inside the sideways chip row.
  useEffect(() => {
    const strip = chipStripRef.current;
    if (!strip) return;
    if (!selectedCategory?._id) {
      strip.scrollTo({ left: 0, behavior: 'smooth' });
      return;
    }
    const el = chipRefs.current[selectedCategory._id];
    if (!el) return;
    const stripRect = strip.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();
    if (elRect.left < stripRect.left || elRect.right > stripRect.right) {
      const target = el.offsetLeft - (strip.clientWidth - el.clientWidth) / 2;
      strip.scrollTo({ left: Math.max(0, target), behavior: 'smooth' });
    }
  }, [selectedCategory?._id]);

  if ((loading && !data) || error) {
    return (
      <Box className={classes.root}>
        <Grid container>
          {isLoggedIn ? <Header /> : <LoginHeader showIcon />}
        </Grid>
        <Box className={classes.spinnerContainer}>
          {error ? (
            <Typography>Unable to fetch data {error.message}</Typography>
          ) : (
            <CircularProgress size={48} sx={{ color: C.red }} />
          )}
        </Box>
      </Box>
    );
  }

  // A food truck opens on its trucks, and a truck on its month of stops. The
  // menu below is only for a stop the customer picked (?stop=).
  // Deterministic avatar color from a name (so the same person keeps the same
  // color across cards), DoorDash-style.
  const AVATAR_COLORS = ['#B3241A', '#6A3D9A', '#2F4B8A', '#0F5C73', '#17633A', '#8A5A00', '#5D4037', '#33312F'];
  const avatarColor = (name) => {
    const str = name || '?';
    let h = 0;
    for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) % AVATAR_COLORS.length;
    return AVATAR_COLORS[h];
  };
  const initials = (name) => {
    const parts = (name || '').trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return '?';
    return (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();
  };
  // First name + last initial, DoorDash-style ("Vivek Thatha" → "Vivek T").
  const displayName = (name) => {
    const parts = (name || '').trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return 'Customer';
    if (parts.length === 1) return parts[0];
    return `${parts[0]} ${parts[parts.length - 1][0].toUpperCase()}`;
  };

  // Round < > pair for a carousel header.
  const CarouselArrows = ({ scrollerRef, label }) => (
    <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 1 }}>
      {[[-1, `Previous ${label}`, ChevronLeftRoundedIcon], [1, `Next ${label}`, ChevronRightRoundedIcon]].map(([dir, aria, Icon]) => (
        <Box
          key={dir}
          component="button"
          type="button"
          onClick={() => scrollerRef.current?.scrollBy({ left: dir * 320, behavior: 'smooth' })}
          aria-label={aria}
          sx={{
            ...BUTTON_RESET,
            width: 44,
            height: 44,
            borderRadius: '50%',
            border: `1px solid ${C.borderStrong}`,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: C.ink,
            '&:hover': { bgcolor: C.surface },
            ...FOCUS_RING,
          }}
        >
          <Icon />
        </Box>
      ))}
    </Box>
  );

  const r = data?.restaurant || {};
  const currency = configuration.currencySymbol;
  const withRestaurant = (food) => ({
    ...food,
    restaurant: restaurantInfo._id,
    restaurantName: restaurantInfo.name,
    deliveryCharges: restaurantInfo.deliveryCharges,
  });
  const thisRestaurantsCart = !restaurantCart || String(restaurantCart) === String(restaurantId);

  // How many of each dish are in the bag, and the lines holding it — the
  // stepper's − takes one off the newest of them.
  const qtyByFood = new Map();
  let bagCount = 0;
  let bagSubtotal = 0;
  if (thisRestaurantsCart) {
    (cart || []).forEach((line) => {
      const qty = parseInt(line.quantity, 10) || 0;
      const entry = qtyByFood.get(String(line._id)) || { qty: 0, keys: [] };
      entry.qty += qty;
      entry.keys.push(line.key);
      qtyByFood.set(String(line._id), entry);
      bagCount += qty;
      bagSubtotal += (parseFloat(line.price) || 0) * qty;
    });
  }
  const qtyOf = (food) => qtyByFood.get(String(food._id))?.qty || 0;
  const openFood = (food) => openFoodModal(withRestaurant(food));
  const addOne = (food) => addFoodToCart(withRestaurant(food), { quiet: qtyOf(food) > 0 });
  const removeOne = (food) => {
    const entry = qtyByFood.get(String(food._id));
    if (entry?.keys.length) removeQuantity(entry.keys[entry.keys.length - 1]);
  };

  // Every dish on the regular menus running today (not catering).
  const regularFoods = menuTypes
    .filter((m) => !m.isCatering && m.availableNow !== false)
    .flatMap((m) => (m.categories || []).flatMap((c) => c.foods || []));

  // Most ordered: each ranked dish matched to its menu item (by id, then by
  // name), so + adds it at the price the menu charges. The open menu first.
  const openMenuFoods = currentCategories.flatMap((c) => c.foods || []);
  const findFood = (mi) =>
    openMenuFoods.find((f) => String(f._id) === String(mi._id))
    || regularFoods.find((f) => String(f._id) === String(mi._id))
    || openMenuFoods.find((f) => f.title === mi.title)
    || regularFoods.find((f) => f.title === mi.title)
    || null;
  const mostOrderedCards = mostOrdered.map((mi) => ({ mi, food: findFood(mi) })).filter((x) => x.food);
  openLinkedItemRef.current = (id) => {
    // Wait for the "closed right now" popup to be dismissed first.
    if (isClose) return false;
    const food = regularFoods.find((f) => String(f._id) === String(id));
    if (!food) return false;
    openFood(food);
    return true;
  };
  const mostOrderedShown = mostOrderedCards.slice(
    0,
    mostOrderedCards.length >= 8 ? 8 : mostOrderedCards.length >= 4 ? 4 : mostOrderedCards.length
  );

  // "Add something small": the cheapest one-tap dishes (one size, nothing to
  // choose) that aren't in the bag yet.
  const upsell = [];
  const upsellSeen = new Set();
  regularFoods
    .filter((f) =>
      !f.is86ed
      && f.variations.length === 1
      && (f.variations[0].addons || []).length === 0
      && f.variations[0].price > 0
      && f.variations[0].price <= 6
      && !qtyByFood.has(String(f._id)))
    .sort((a, b) => a.variations[0].price - b.variations[0].price)
    .forEach((f) => {
      if (upsell.length < 3 && !upsellSeen.has(String(f._id))) {
        upsellSeen.add(String(f._id));
        upsell.push(f);
      }
    });

  // "Search this menu".
  const q = menuQuery.trim().toLowerCase();
  const matchesQuery = (f) => [f.title, f.description, f.altName1, f.altName2].some((t) => (t || "").toLowerCase().includes(q));
  const shownCategories = q
    ? currentCategories.map((c) => ({ ...c, foods: (c.foods || []).filter(matchesQuery) })).filter((c) => c.foods.length)
    : currentCategories;
  const resultCount = q ? shownCategories.reduce((n, c) => n + c.foods.length, 0) : 0;
  const menuRunsToday = menuTypes[selectedMenu]?.availableNow !== false;

  // ── Header status chips ───────────────────────────────────────────────
  const nowWall = wallParts(new Date(), r.timezone);
  const nowMin = nowWall.minutes;
  const todays = (r.openingTimes || []).find((o) => o.day === DAYS[nowWall.dayIdx]);
  const windows = (todays?.times || []).filter((t) => t?.startTime && t?.endTime);
  const svc = r.serviceLocation;
  let status;
  if (!restaurantInfo.isAvailable) {
    status = { label: "Not taking orders", tone: "warn" };
  } else if (r.isMobile) {
    const win = orderingStop?.stop?.windowLabel || svc?.windowLabel;
    status = r.isOpenNow
      ? { label: win ? `Open · until ${win.split("–").pop().trim()}` : "Open now", tone: "open" }
      : { label: svc?.nextStopLabel || "Not out today", tone: "closed" };
  } else if (isOpen()) {
    const current = windows.find((t) => nowMin >= minutesOf(t.startTime) && nowMin <= minutesOf(t.endTime));
    status = { label: current ? `Open · until ${clock(current.endTime)}` : "Open", tone: "open" };
  } else {
    const later = windows
      .filter((t) => minutesOf(t.startTime) > nowMin)
      .sort((a, b) => minutesOf(a.startTime) - minutesOf(b.startTime))[0];
    status = {
      label: later ? `Closed · opens ${clock(later.startTime)}` : windows.length ? "Closed now" : "Closed today",
      tone: "closed",
    };
  }
  const heroChips = [];
  const hoursLabel = r.isMobile
    ? orderingStop?.stop?.windowLabel || (r.isOpenNow ? svc?.windowLabel : null)
    : windows.map((t) => `${clock(t.startTime)} – ${clock(t.endTime)}`).join(", ");
  if (hoursLabel) heroChips.push({ key: "hours", icon: CHIP_ICONS.hours, label: hoursLabel });
  const prep = r.preparationTime ?? r.deliveryTime;
  if (isPickup && prep) heroChips.push({ key: "eta", icon: CHIP_ICONS.eta, label: `Ready in ${prep} min` });
  if (!isPickup && r.deliveryTime) heroChips.push({ key: "eta", icon: CHIP_ICONS.eta, label: `${r.deliveryTime} min` });
  if (isPickup) {
    heroChips.push(r.enablePickup === false
      ? { key: "fee", icon: CHIP_ICONS.pickup, label: "Delivery only", tone: "warn" }
      : { key: "fee", icon: CHIP_ICONS.pickup, label: "Pickup · no fee" });
  } else if (r.enableDelivery === false) {
    heroChips.push({ key: "fee", icon: CHIP_ICONS.delivery, label: "Pickup only", tone: "warn" });
  } else if (deliveryQuote) {
    const fee = parseFloat(deliveryQuote.fee) || 0;
    heroChips.push(deliveryQuote.available
      ? { key: "fee", icon: CHIP_ICONS.delivery, label: fee > 0 ? `${currency}${fee.toFixed(2)} delivery` : "Free delivery" }
      : { key: "fee", icon: CHIP_ICONS.delivery, label: deliveryQuote.noZonesConfigured ? "Not delivering right now" : "Outside delivery area", tone: "warn" });
  }
  const cuisineLine = (r.cuisines || []).filter(Boolean).slice(0, 3).join(" · ");

  const handleFavourite = () => {
    if (!profile) {
      showMessage({ type: "error", message: "Please log in to add favorites" });
      return;
    }
    if (!favouriteSaving && restaurantId) toggleFavourite({ variables: { id: restaurantId } });
  };
  const handleShare = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({ title: restaurantInfo.name, url });
        return;
      }
      await navigator.clipboard.writeText(url);
      showMessage({ type: "success", message: "Link copied" });
    } catch (e) {
      // Share sheet dismissed, or the clipboard is blocked — nothing to do.
    }
  };

  const cartProps = {
    showMessage,
    restaurantId,
    promos: restaurantPromos,
    upsell,
    onAddItem: (food) => addFoodToCart(withRestaurant(food)),
  };

  const noticeSx = (tone) => ({
    mt: 2,
    p: 2,
    borderRadius: RADIUS.controlLg,
    display: "flex",
    gap: 1.25,
    alignItems: "flex-start",
    ...TYPE.body,
    ...(tone === "warn" ? { bgcolor: C.redSoft, color: C.redLink } : { bgcolor: C.surface, color: C.ink }),
  });

  // A food truck opens on its trucks (hero, deals, trucks beside a map), and
  // a truck on its month of stops. The menu below is only for a stop the
  // customer picked (?stop=). Placed after the favourite/share handlers,
  // which it uses; no hooks follow, so returning here is safe.
  if (showTruckPages) {
    return (
      <>
        {seo && <Seo {...seo} />}
        <FlashMessage
          open={Boolean(mainError.type)}
          severity={mainError.type}
          alertMessage={mainError.message}
          handleClose={toggleSnackbar}
        />
        <Box sx={{ bgcolor: C.bg, minHeight: "100vh", color: C.ink, fontFamily: FONT_BODY }}>
          {isLoggedIn ? <Header /> : <LoginHeader showIcon />}
          {truckId ? (
            <TruckSchedulePage
              restaurant={data.restaurant}
              truckId={truckId}
              month={truckMonth}
              onMonth={(month) => goTruckPage({ truck: truckId, month })}
              onBack={() => goTruckPage({})}
              onOrderStop={(day) => goTruckPage({ stop: day.stop._id, truck: truckId, day: day.date })}
            />
          ) : (
            <TruckLanding
              restaurant={data.restaurant}
              cuisineLine={cuisineLine}
              promos={restaurantPromos}
              rewardsPoints={rewardsPoints}
              showSeeAllDeals={!isEmbed}
              isFavourite={isFavourite}
              onToggleFavourite={handleFavourite}
              onShare={handleShare}
              onOpenTruck={(id) => goTruckPage({ truck: id })}
            />
          )}
        </Box>
      </>
    );
  }

  return (
    <>
      {seo && <Seo {...seo} />}
      <FlashMessage
        open={Boolean(mainError.type)}
        severity={mainError.type}
        alertMessage={mainError.message}
        handleClose={toggleSnackbar}
      />
      <Box sx={{ bgcolor: C.bg, minHeight: "100vh", color: C.ink, fontFamily: FONT_BODY }}>
        {isLoggedIn ? <Header /> : <LoginHeader showIcon />}

        <Box
          sx={{
            maxWidth: 1440,
            mx: "auto",
            px: { xs: 2, md: 3 },
            pt: topBarOffset(16),
            pb: isTablet && bagCount > 0 ? 14 : 8,
            display: "grid",
            gridTemplateColumns: { xs: "minmax(0, 1fr)", lg: "minmax(0, 1fr) 380px", xl: "minmax(0, 1fr) 400px" },
            columnGap: 4,
            alignItems: "start",
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <RestaurantHero
              name={restaurantInfo.name}
              image={restaurantInfo.image}
              logo={r.logo}
              cuisineLine={cuisineLine}
              isFoodTruck={r.isMobile === true}
              status={status}
              chips={heroChips}
              isFavourite={isFavourite}
              onToggleFavourite={handleFavourite}
              onShare={handleShare}
              onStoreInfo={toggleReviewModal}
              storeInfoLabel={r.isMobile ? "Food truck info" : "Store info"}
              actions={<ReserveTableButton restaurantId={restaurantId} slug={r.slug} />}
            />

            {/* A food-truck stop's menu: which truck, where, when, and
                whether it takes orders today. */}
            {orderingStop && (
              <Box sx={{ mt: 3 }}>
                <StopBanner
                  orderingStop={orderingStop}
                  viewingDay={stopDay}
                  onChange={() => goTruckPage(orderingStop.truckId || truckId ? { truck: orderingStop.truckId || truckId } : {})}
                />
              </Box>
            )}

            {/* Food truck: where it is parked today. A truck's address changes
                daily, so this has to be on the page itself — not buried in the
                Store info modal where a fixed address normally lives. */}
            {r.isMobile && !orderingStop && (
              <Box
                sx={{
                  mt: 3,
                  p: 2,
                  borderRadius: RADIUS.card,
                  border: `1px solid ${r.isOpenNow ? "transparent" : C.border}`,
                  bgcolor: r.isOpenNow ? C.greenSoft : C.surface,
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                }}
              >
                <LocalShippingRoundedIcon aria-hidden="true" sx={{ fontSize: 28, color: r.isOpenNow ? C.green : C.ink }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ ...TYPE.itemName, color: C.ink }}>
                    {r.isOpenNow
                      ? (svc?.label ? `Today at ${svc.label}` : "Parked now")
                      : (svc?.nextStopLabel || "Not out today")}
                  </Box>
                  {!!svc?.address && (
                    <Box sx={{ ...TYPE.meta, color: C.muted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {svc.address}
                      {r.isOpenNow && svc.windowLabel ? ` · until ${svc.windowLabel.split("–").pop().trim()}` : ""}
                    </Box>
                  )}
                </Box>
                {svc?.latitude != null && (
                  <Box
                    component="a"
                    href={`https://www.google.com/maps/dir/?api=1&destination=${svc.latitude},${svc.longitude}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      height: 44,
                      px: 2,
                      borderRadius: RADIUS.control,
                      border: `1px solid ${C.borderStrong}`,
                      bgcolor: C.bg,
                      color: C.ink,
                      textDecoration: "none",
                      fontFamily: FONT_BODY,
                      fontSize: 14,
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                      ...FOCUS_RING,
                    }}
                  >
                    Directions
                  </Box>
                )}
              </Box>
            )}

            <DealBanners promos={restaurantPromos} rewardsPoints={rewardsPoints} />

            {/* Reviews — when there are any ratings. The carousel lists only
                written reviews; the average tile always shows. */}
            {reviewList.length > 0 && (
              <Box component="section" aria-labelledby="reviews-title" sx={{ mt: { xs: 4, md: 5 } }} id="section-reviews">
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                  <Box>
                    <Box component="h2" id="reviews-title" sx={{ ...TYPE.sectionTitle, m: 0 }}>Reviews</Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5, ...TYPE.meta, color: C.muted }}>
                      {reviewAvg > 0 && (
                        <>
                          <StarIcon aria-hidden="true" sx={{ fontSize: 16, color: C.goldAccent }} />
                          <Box component="span" sx={{ fontWeight: 700, color: C.ink }}>{reviewAvg.toFixed(1)}</Box>
                          <span aria-hidden="true">·</span>
                        </>
                      )}
                      <span>
                        {totalRatings} ratings
                        {writtenReviews.length > 0 && ` · ${writtenReviews.length} public review${writtenReviews.length === 1 ? '' : 's'}`}
                      </span>
                    </Box>
                  </Box>
                  <CarouselArrows scrollerRef={reviewsScrollerRef} label="reviews" />
                </Box>
                <Box ref={reviewsScrollerRef} sx={{ display: 'flex', gap: 2, overflowX: 'auto', pb: 1, scrollSnapType: 'x mandatory', scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}>
                  {reviewAvg > 0 && (
                    <Box sx={{ flex: '0 0 auto', width: 120, p: 2, borderRadius: RADIUS.menuCard, bgcolor: C.surface, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Box component="span" sx={{ ...TYPE.pageTitle, fontSize: 28 }}>{reviewAvg.toFixed(1)}</Box>
                        <StarIcon aria-hidden="true" sx={{ fontSize: 22, color: C.ink }} />
                      </Box>
                      <Box sx={{ ...TYPE.meta, color: C.muted, mt: 0.5 }}>of 5 stars</Box>
                    </Box>
                  )}
                  {writtenReviews.map((rev) => {
                    const name = rev.user?.name || 'Customer';
                    return (
                      <Box
                        key={rev._id}
                        sx={{ flex: '0 0 auto', width: 280, p: 2, borderRadius: RADIUS.menuCard, border: `1px solid ${C.border}`, scrollSnapAlign: 'start' }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1 }}>
                          <Box
                            aria-hidden="true"
                            sx={{
                              flexShrink: 0, width: 36, height: 36, borderRadius: '50%',
                              bgcolor: avatarColor(name), color: '#fff',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              fontFamily: FONT_BODY, fontSize: 13, fontWeight: 700,
                            }}
                          >
                            {initials(name)}
                          </Box>
                          <Box sx={{ minWidth: 0 }}>
                            <Box sx={{ ...TYPE.itemName, fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {displayName(name)}
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', color: C.goldAccent }} aria-label={`${rev.rating || 0} out of 5 stars`}>
                              {Array.from({ length: rev.rating || 0 }).map((_, i) => (
                                <StarIcon key={i} aria-hidden="true" sx={{ fontSize: 14 }} />
                              ))}
                              {rev.createdAt && (
                                <Box component="span" sx={{ ...TYPE.label, fontWeight: 500, color: C.muted, ml: 0.75 }}>
                                  {new Date(isNaN(Number(rev.createdAt)) ? rev.createdAt : Number(rev.createdAt)).toLocaleDateString()}
                                </Box>
                              )}
                            </Box>
                          </Box>
                        </Box>
                        <Box sx={{ ...TYPE.body, color: C.muted, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                          {rev.description}
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            )}

            {/* ── Menu ─────────────────────────────────────────────────── */}
            <Box component="section" aria-labelledby="menu-title" ref={menuSectionRef} sx={{ mt: { xs: 4, md: 5 } }}>
              <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
                <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 2, minWidth: 0, maxWidth: "100%" }}>
                  <Box component="h2" id="menu-title" sx={{ ...TYPE.sectionTitle, m: 0 }}>Menu</Box>
                  {menuTypes.length > 1 ? (
                    <Box
                      role="group"
                      aria-label="Choose a menu"
                      sx={{
                        display: "inline-flex",
                        gap: "4px",
                        p: "4px",
                        maxWidth: "100%",
                        borderRadius: RADIUS.controlLg,
                        bgcolor: C.surface2,
                        overflowX: "auto",
                        scrollbarWidth: "none",
                        "&::-webkit-scrollbar": { display: "none" },
                      }}
                    >
                      {menuTypes.map((menu, index) => {
                        const active = selectedMenu === index;
                        const hours = menu.isCatering
                          ? null
                          : menu.timeLabel
                            ? (menu.availableNow ? menu.timeLabel.replace(/^Available /, '') : menu.timeLabel.replace('Available', 'Starts'))
                            : null;
                        return (
                          <Box
                            key={menu.isCatering ? 'catering' : index}
                            component="button"
                            type="button"
                            onClick={() => handleSelectMenu(index)}
                            aria-pressed={active}
                            sx={{
                              ...BUTTON_RESET,
                              flexShrink: 0,
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "flex-start",
                              justifyContent: "center",
                              minHeight: 44,
                              px: 2,
                              py: 0.5,
                              borderRadius: "10px",
                              bgcolor: active ? C.red : "transparent",
                              color: active ? "#FFFFFF" : C.ink,
                              opacity: !menu.availableNow && !active ? 0.7 : 1,
                              transition: "background-color .15s ease",
                              "&:hover": { bgcolor: active ? C.redHover : C.border },
                              ...FOCUS_RING,
                            }}
                          >
                            <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, fontFamily: FONT_BODY, fontSize: 14, fontWeight: 700, whiteSpace: "nowrap" }}>
                              {menu.isCatering && <RoomServiceRoundedIcon aria-hidden="true" sx={{ fontSize: 17 }} />}
                              {menu.isCatering ? 'Catering' : menu.name}
                            </Box>
                            {hours && (
                              <Box component="span" sx={{ fontFamily: FONT_BODY, fontSize: 12, fontWeight: 500, whiteSpace: "nowrap", color: active ? "#FFFFFF" : C.muted }}>
                                {hours}
                              </Box>
                            )}
                          </Box>
                        );
                      })}
                    </Box>
                  ) : menuTypes[0]?.timeLabel ? (
                    <Box component="span" sx={{ ...TYPE.meta, color: C.muted }}>
                      {`${menuTypes[0].name} · ${menuTypes[0].timeLabel.replace(/^Available /, '')}`}
                    </Box>
                  ) : null}
                </Box>
                <Box sx={{ display: "flex", width: { xs: "100%", sm: 320 } }}>
                  <HeaderSearchBar
                    value={menuQuery}
                    onChange={(e) => setMenuQuery(e.target.value)}
                    placeholder="Search this menu"
                  />
                </Box>
              </Box>

              {/* Holiday or planned closing. The kitchen is shut for these
                  dates, so the backend refuses orders scheduled into them —
                  say so here rather than letting checkout fail. */}
              {r.activeClosure && (
                <Box sx={noticeSx("warn")} role="note">
                  <EventBusyOutlinedIcon aria-hidden="true" sx={{ fontSize: 20, mt: "1px" }} />
                  <Box>
                    <Box sx={{ fontWeight: 700 }}>{`Closed today — ${r.activeClosure.name}`}</Box>
                    <Box>{`${r.activeClosure.dateLabel}. Orders cannot be placed for these dates.`}</Box>
                  </Box>
                </Box>
              )}
              {!r.activeClosure && (r.upcomingClosures || []).length > 0 && (
                <Box sx={noticeSx()} role="note">
                  <EventBusyOutlinedIcon aria-hidden="true" sx={{ fontSize: 20, mt: "1px", color: C.muted }} />
                  <Box>
                    {`Upcoming: ${r.upcomingClosures
                      .map((c) => `${c.name} (${(c.dateLabel || '').replace(/^Closed /, '')})`)
                      .join(' · ')}`}
                  </Box>
                </Box>
              )}

              {/* The open menu doesn't run today. */}
              {menuTypes[selectedMenu] && !menuTypes[selectedMenu].availableNow && menuTypes[selectedMenu].timeLabel && (
                <Box sx={noticeSx()} role="note">
                  <ScheduleRoundedIcon aria-hidden="true" sx={{ fontSize: 20, mt: "1px", color: C.muted }} />
                  <Box sx={{ fontWeight: 600 }}>
                    {menuTypes[selectedMenu].timeLabel.replace('Available', 'This menu is available from')}
                  </Box>
                </Box>
              )}

              {/* Catering view: notice, how it is delivered, the minimum; then
                  quick picks, the items that say how many they serve. */}
              {isCateringView && (
                <CateringMenuHeader
                  settings={cateringSettings}
                  currencySymbol={currency}
                />
              )}
              {isCateringView && (
                <CateringSuggestions
                  foods={(cateringMenu?.categories || []).flatMap((c) => c.foods)}
                  guests={cateringGuests}
                  onOpen={(food) => openFood(food)}
                  currencySymbol={currency}
                />
              )}

              {/* Sticky category chips, right under the top bar. */}
              {!q && menuRunsToday && currentCategories.length > 0 && (
                <Box
                  ref={chipsBarRef}
                  sx={{
                    position: "sticky",
                    top: topBarOffset(0),
                    zIndex: 5,
                    bgcolor: C.bg,
                    mt: 2,
                    mx: { xs: -2, md: 0 },
                    px: { xs: 2, md: 0 },
                    py: 1.5,
                    borderBottom: `1px solid ${C.border}`,
                  }}
                >
                  <Box
                    component="nav"
                    aria-label="Menu categories"
                    ref={chipStripRef}
                    sx={{
                      display: "flex",
                      gap: 1,
                      overflowX: "auto",
                      scrollSnapType: "x proximity",
                      scrollbarWidth: "none",
                      "&::-webkit-scrollbar": { display: "none" },
                    }}
                  >
                    {currentCategories.map((cat) => {
                      const active = selectedCategory?._id === cat._id;
                      return (
                        <Box
                          key={cat._id}
                          component="button"
                          type="button"
                          ref={(el) => { if (el) chipRefs.current[cat._id] = el; }}
                          onClick={() => handleCategoryClick(cat)}
                          aria-current={active ? "true" : undefined}
                          sx={{
                            ...BUTTON_RESET,
                            flexShrink: 0,
                            scrollSnapAlign: "start",
                            display: "inline-flex",
                            alignItems: "center",
                            height: 40,
                            my: "2px",
                            px: 2,
                            borderRadius: RADIUS.pill,
                            bgcolor: active ? C.red : C.surface2,
                            color: active ? "#FFFFFF" : C.ink,
                            fontFamily: FONT_BODY,
                            fontSize: 14,
                            fontWeight: 600,
                            whiteSpace: "nowrap",
                            transition: "background-color .15s ease, color .15s ease",
                            "&:hover": { bgcolor: active ? C.redHover : C.border },
                            ...FOCUS_RING,
                          }}
                        >
                          {cat.title}
                          <AltNames alt1={cat.altTitle1} alt2={cat.altTitle2} displayMode={langDisplay} />
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              )}

              {q && (
                <Box role="status" sx={{ mt: 2, display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1.5, ...TYPE.meta, color: C.muted }}>
                  <span>
                    {resultCount
                      ? `${resultCount} ${resultCount === 1 ? "dish matches" : "dishes match"} "${menuQuery.trim()}"`
                      : `No dishes match "${menuQuery.trim()}"`}
                  </span>
                  <Box
                    component="button"
                    type="button"
                    onClick={() => setMenuQuery("")}
                    sx={{ ...BUTTON_RESET, height: 44, fontWeight: 600, color: C.redLink, ...FOCUS_RING }}
                  >
                    Clear search
                  </Box>
                </Box>
              )}

              {/* Most ordered — only when we have ranked items. Not at a
                  truck stop (its menu and prices are the stop's own), nor on
                  the Catering view. */}
              {!q && menuRunsToday && !stopId && !isCateringView && mostOrderedShown.length > 0 && (
                <Box component="section" aria-labelledby="most-ordered-title" id="section-most-ordered" sx={{ mt: 3 }}>
                  <Box component="h3" id="most-ordered-title" sx={{ ...TYPE.cardTitle, m: 0 }}>Most ordered</Box>
                  <Box component="p" sx={{ ...TYPE.body, color: C.muted, m: 0, mt: 0.25 }}>
                    The most commonly ordered items and dishes from this store
                  </Box>
                  <Box
                    sx={{
                      mt: 2,
                      display: "grid",
                      gap: 2,
                      gridTemplateColumns: { xs: "minmax(0, 1fr)", sm: "repeat(2, minmax(0, 1fr))", md: "repeat(4, minmax(0, 1fr))" },
                    }}
                  >
                    {mostOrderedShown.map(({ mi, food }) => (
                      <MostOrderedCard
                        key={mi._id}
                        item={mi}
                        food={food}
                        quantity={qtyOf(food)}
                        onOpen={() => openFood(food)}
                        onAdd={() => addOne(food)}
                        onRemove={() => removeOne(food)}
                        currencySymbol={currency}
                        langDisplay={langDisplay}
                      />
                    ))}
                  </Box>
                </Box>
              )}

              {/* Categories, each a two-column grid of dish cards. */}
              {menuRunsToday && shownCategories.map((category) => (
                <Box
                  component="section"
                  key={category._id}
                  id={`category-${category._id}`}
                  aria-labelledby={`category-title-${category._id}`}
                  sx={{ mt: 4 }}
                >
                  <Box component="h3" id={`category-title-${category._id}`} sx={{ ...TYPE.cardTitle, m: 0 }}>
                    {category.title}
                    <AltNames alt1={category.altTitle1} alt2={category.altTitle2} displayMode={langDisplay} />
                  </Box>
                  {category.description && (
                    <Box component="p" sx={{ ...TYPE.body, color: C.muted, m: 0, mt: 0.25 }}>
                      {category.description}
                    </Box>
                  )}
                  <Box
                    sx={{
                      mt: 1.5,
                      display: "grid",
                      gap: 2,
                      gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "repeat(2, minmax(0, 1fr))" },
                    }}
                  >
                    {category.foods.map((food, foodIndex) => (
                      <MenuItemCard
                        key={`${food._id}_${foodIndex}`}
                        food={food}
                        quantity={qtyOf(food)}
                        onOpen={() => openFood(food)}
                        onAdd={() => addOne(food)}
                        onRemove={() => removeOne(food)}
                        currencySymbol={currency}
                        langDisplay={langDisplay}
                        cateringTarget={!food.isCatering && food.cateringItemId ? cateringTargets.get(food.cateringItemId) || null : null}
                        onOpenCatering={openCateringItem}
                      />
                    ))}
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>

          {/* Wide screens: the cart is a sticky right column, never taller
              than the window — its middle scrolls, checkout stays in view. */}
          {!isTablet && (
            <Box
              component="aside"
              aria-label="Your order"
              sx={{ position: "sticky", top: topBarOffset(16) }}
            >
              <RestaurantCart {...cartProps} maxHeight={`calc(100vh - var(--rmf-topbar-h, 64px) - 32px)`} />
            </Box>
          )}
        </Box>

        {/* Modals */}
        <RestaurantClose
          isVisible={isClose}
          toggleModal={toggleCloseModal}
          restaurant={{ ...r, name: restaurantInfo.name, isAvailable: restaurantInfo.isAvailable }}
        />
        <VariationModal
          isVisible={variationModal}
          toggleModal={toggleVariationModal}
          data={addonData}
          langDisplay={langDisplay}
          onAdded={() => showMessage({ type: 'success', message: `${addonData?.food?.title ?? 'Item'} added to cart` })}
          confirmCartKind={confirmCartKind}
        />
        <StopCartDialog prompt={stopCartPrompt} onAnswer={answerStopCart} />
        <CateringCartDialog
          open={Boolean(cartKindPrompt?.open)}
          toCatering={Boolean(cartKindPrompt?.toCatering)}
          onConfirm={() => answerCartKind({ clear: true })}
          onCancel={() => answerCartKind(null)}
        />
        <RestaurantInfo
          isVisible={reviewModal}
          toggleModal={toggleReviewModal}
          restaurantInfo={restaurantInfo}
        />
        {/* Narrow screens: "View cart · $total" bar + bottom sheet. Same
            guard as the wide panel: a cart adopted from another restaurant
            never surfaces on this restaurant's page. */}
        {isTablet && (
          <CartSheet
            open={cartSheetOpen && bagCount > 0}
            onOpen={() => setCartSheetOpen(true)}
            onClose={() => setCartSheetOpen(false)}
            count={bagCount}
            totalLabel={`${currency}${bagSubtotal.toFixed(2)}`}
            showBar={bagCount > 0 && !cartSheetOpen}
          >
            <RestaurantCart {...cartProps} flat maxHeight="calc(92vh - 28px)" />
          </CartSheet>
        )}
      </Box>
    </>
  );
}

export default RestaurantDetail;
